import type { MarketingGranularity } from '@/lib/api/marketing/marketing-dashboard-api'

/** Valores em centavos, como em todo o sistema. */
export const formatCurrency = (cents: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100)

export const formatCurrencyCompact = (cents: number) => {
    const value = cents / 100
    const abs = Math.abs(value)
    if (abs >= 1_000_000) {
        return `R$ ${(value / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} mi`
    }
    if (abs >= 10_000) {
        return `R$ ${(value / 1_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`
    }
    return formatCurrency(cents)
}

export const formatNumber = (value: number) => new Intl.NumberFormat('pt-BR').format(value)

export const formatPercent = (value: number | null) =>
    value == null ? '—' : `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`

/** Duração legível: "45 s", "3 min", "1 h 20 min", "2 d 4 h". */
export function formatDuration(seconds: number | null): string {
    if (seconds == null) return '—'
    const s = Math.round(seconds)
    if (s < 60) return `${s} s`
    const minutes = Math.round(s / 60)
    if (minutes < 60) return `${minutes} min`
    const hours = Math.floor(minutes / 60)
    const restMin = minutes % 60
    if (hours < 24) return restMin ? `${hours} h ${restMin} min` : `${hours} h`
    const days = Math.floor(hours / 24)
    const restH = hours % 24
    return restH ? `${days} d ${restH} h` : `${days} d`
}

const pad = (n: number) => String(n).padStart(2, '0')
const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']

/** `YYYY-MM-DD` -> `dd/MM/yyyy`, sem passar por `Date` (que aplicaria fuso). */
export function formatDay(day: string): string {
    const [y, m, d] = day.split('-')
    return `${d}/${m}/${y}`
}

export function formatRange(range: { startDay: string; endDay: string }): string {
    return range.startDay === range.endDay
        ? formatDay(range.startDay)
        : `${formatDay(range.startDay)} a ${formatDay(range.endDay)}`
}

/** Rótulo do balde da série (chaves geradas no backend, já no fuso de Brasília). */
export function formatBucket(key: string, granularity: MarketingGranularity): string {
    switch (granularity) {
        case 'hour': {
            const [, hour] = key.split(' ')
            return `${hour}h`
        }
        case 'month': {
            const [y, m] = key.split('-')
            return `${MONTHS[Number(m) - 1]}/${y.slice(2)}`
        }
        case 'week': {
            const [, m, d] = key.split('-')
            return `sem. ${d}/${m}`
        }
        default: {
            const [, m, d] = key.split('-')
            return `${pad(Number(d))}/${pad(Number(m))}`
        }
    }
}

/** Rótulo longo do balde, para o tooltip (inclui o ano e a data inteira). */
export function formatBucketLong(key: string, granularity: MarketingGranularity): string {
    switch (granularity) {
        case 'hour': {
            const [day, hour] = key.split(' ')
            return `${formatDay(day)} ${hour}h`
        }
        case 'month': {
            const [y, m] = key.split('-')
            return `${MONTHS[Number(m) - 1]}/${y}`
        }
        case 'week':
            return `semana de ${formatDay(key)}`
        default:
            return formatDay(key)
    }
}

export type Polarity = 'higher-better' | 'lower-better' | 'neutral'

export interface Delta {
    /** Variação em %; `null` quando não há base (anterior zero ou ausente). */
    pct: number | null
    diff: number
    /** O anterior era zero e o atual não: "novo". */
    fromZero: boolean
    tone: 'good' | 'bad' | 'neutral'
}

export function deltaOf(
    current: number | null | undefined,
    previous: number | null | undefined,
    polarity: Polarity = 'higher-better',
): Delta | null {
    if (current == null || previous == null) return null
    const diff = current - previous
    const fromZero = previous === 0 && current !== 0
    const pct = previous === 0 ? null : (diff / Math.abs(previous)) * 100
    let tone: Delta['tone'] = 'neutral'
    if (polarity !== 'neutral' && Math.abs(diff) > 0 && (pct == null || Math.abs(pct) >= 0.5)) {
        const up = diff > 0
        tone = (polarity === 'higher-better') === up ? 'good' : 'bad'
    }
    return { pct, diff, fromZero, tone }
}
